import React, { useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faTimes } from '@fortawesome/free-solid-svg-icons';
import { Table, createCustomColumn, createTextColumn, useTableState } from '../deps.js';

const useAdminClinics = (directory) => {
  const { clinics, adminSelectedClinicId, setAdminSelectedClinicId, isMaster, tableIds } = directory;

  const {
    sortState,
    setSortState,
    filterValues,
    setFilterValues,
    disabledFilters,
    setDisabledFilters,
    paginationState,
    setPaginationState,
  } = useTableState(tableIds.clinics, isMaster ? {} : {
    defaultSortState: { columnKey: 'clinicName', direction: 'asc' },
  });

  const highlightedRows = useMemo(
    () => (adminSelectedClinicId ? new Set([adminSelectedClinicId]) : new Set()),
    [adminSelectedClinicId]
  );

  const columns = useMemo(() => {
    const nameColumn = createTextColumn('clinicName', 'Clinic Name', (c) => c.clinicName, { filterable: true });
    const productColumns = [
      createCustomColumn(
        'documents',
        'Documents',
        (clinic) => Array.isArray(clinic.products) && clinic.products.includes('DOCUMENTS'),
        (value) => (
          value ? (
            <FontAwesomeIcon icon={faCheck} className="admin-ability-check" />
          ) : (
            <FontAwesomeIcon icon={faTimes} className="admin-ability-times" />
          )
        ),
        { sortable: true, filterable: false, className: 'admin-ability-column' }
      ),
      createCustomColumn(
        'scribe',
        'Scribe',
        (clinic) => Array.isArray(clinic.products) && clinic.products.includes('SCRIBE'),
        (value) => (
          value ? (
            <FontAwesomeIcon icon={faCheck} className="admin-ability-check" />
          ) : (
            <FontAwesomeIcon icon={faTimes} className="admin-ability-times" />
          )
        ),
        { sortable: true, filterable: false, className: 'admin-ability-column' }
      ),
    ];

    if (isMaster) {
      return [
        createTextColumn('clinicId', 'Clinic ID', (c) => c.clinicId),
        nameColumn,
        ...productColumns,
      ];
    }

    return [
      nameColumn,
      createTextColumn('userCount', 'Users', (c) => String(c.userCount ?? 0), { filterable: false }),
      createTextColumn('teamCount', 'Teams', (c) => String(c.teamCount ?? 0), { filterable: false }),
      ...productColumns,
    ];
  }, [isMaster]);

  const section = (!isMaster && clinics.length <= 1) ? null : (
    <div className="admin-table-section">
      <h2 className="admin-table-title">Clinics</h2>
      <Table
        id={tableIds.clinics}
        data={clinics}
        columns={columns}
        getRowKey={(c) => c.clinicId}
        sortState={sortState}
        onSortChange={setSortState}
        filterValues={filterValues}
        onFilterChange={setFilterValues}
        disabledFilters={disabledFilters}
        onDisabledFiltersChange={setDisabledFilters}
        paginationState={paginationState}
        onPaginationChange={setPaginationState}
        onRowClick={(clinic) => setAdminSelectedClinicId(clinic.clinicId)}
        highlightedRows={highlightedRows}
        emptyMessage="No clinics found."
      />
    </div>
  );

  return { section };
};

export default useAdminClinics;
